import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

// Same query key hooks/useAdminRequests.js's own useAdminSalesUsers() already
// uses for GET /admin/users (that hook backs the "all requests" screen's
// assignee picker) — kept identical on purpose, not duplicated by accident,
// so a create/deactivate mutation here also invalidates that picker's cache
// instead of leaving it stale until its own unrelated refetch. TanStack Query
// keys are compared by value, not by importing a shared constant across two
// hook files — this repo's other hook files (e.g. useApprovalQueue.js /
// useApprovalDetail.js) don't share a constant either, they just agree on
// the same array literal.
const USERS_KEY = ['admin', 'salesUsers'];

// GET /admin/users — SALES-role accounts only, active and inactive both
// (backend/src/services/userService.js#listSalesUsers). This screen's own
// scope (admin console screen 4 of 5, route /users): create and deactivate
// sales accounts, nothing else — see pages/UsersPage.jsx's own header
// comment. Unlike CatalogPage's product list (server-filtered to
// is_active=1), inactive accounts are NOT filtered out here — the page
// itself decides how to render them (dimmed row, still listed).
export function useAdminUsers() {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: () => apiClient.get('/admin/users'),
    select: (data) => data.users,
  });
}

// Multi-select bulk-actions task: the Users MANAGEMENT screen's own list
// (pages/UsersPage.jsx), broadened to also include admin accounts — a
// DIFFERENT, deliberately separate query key from USERS_KEY above, so this
// never shares a cache entry with the assignee picker's sales-only
// useAdminUsers()/useAdminSalesUsers() (mixing admin rows into that picker's
// cache would let a request be assigned to an admin, which is wrong). Calls
// the same GET /admin/users endpoint with `?includeAdmins=1` — see
// backend/src/services/userService.js#listStaffUsers's own comment on why
// that's opt-in.
const STAFF_USERS_KEY = ['admin', 'staffUsers'];

export function useAdminStaffUsers() {
  return useQuery({
    queryKey: STAFF_USERS_KEY,
    queryFn: () => apiClient.get('/admin/users', { includeAdmins: '1' }),
    select: (data) => data.users,
  });
}

// Invalidates BOTH the assignee-picker's sales-only cache and the Users
// screen's broader staff cache — a mutation on this endpoint (create/
// deactivate/reactivate/delete/bulk-*) can change either view's data
// regardless of which screen triggered it (e.g. deactivating a sales user
// from the Users screen must also refresh the assignee picker so a
// newly-inactive account stops looking selectable there next time it
// refetches).
function invalidateStaffCaches(queryClient) {
  queryClient.invalidateQueries({ queryKey: USERS_KEY });
  queryClient.invalidateQueries({ queryKey: STAFF_USERS_KEY });
}

// POST /admin/users — {fullName, email, phone?, password, role}. `role` is
// 'sales' | 'catalog_manager' (migration 025 — routes/adminUserRoutes.js's
// own createUserSchema); UserForm.jsx's role picker is what actually
// decides it per submission, this hook just forwards the body unchanged.
// Kept this name (not renamed to a generic useCreateStaffUser) to avoid
// churn in every existing caller — the exported function creates whichever
// staff role the caller's body asks for, same as the backend route it
// calls. Merge-then-invalidate: the new row is appended to the cache
// immediately for instant feedback, then the query is invalidated so the
// real, full-name-sorted order (userRepository.listByRole's own ORDER BY
// full_name ASC) catches up — same two-step pattern as
// useAdminRequests.js#useAssignRequest.
export function useCreateSalesUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiClient.post('/admin/users', body),
    onSuccess: (data) => {
      queryClient.setQueryData(USERS_KEY, (prev) => {
        if (!prev || !prev.users) return prev;
        return { ...prev, users: [...prev.users, data.user] };
      });
      invalidateStaffCaches(queryClient);
    },
  });
}

// POST /admin/users/:id/deactivate — no body. Works for any staff role now
// (backend/src/services/userService.js#deactivateStaffUser) — the name
// stays "StaffUser", not "SalesUser", to match.
export function useDeactivateStaffUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`/admin/users/${id}/deactivate`),
    onSuccess: () => invalidateStaffCaches(queryClient),
  });
}

// POST /admin/users/:id/reactivate — no body. New for the "a mistaken
// deactivation is recoverable" requirement.
export function useReactivateStaffUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`/admin/users/${id}/reactivate`),
    onSuccess: () => invalidateStaffCaches(queryClient),
  });
}

// Bulk actions (multi-select task). No optimistic cache merge — a bulk
// result is a mixed ok/skipped list, not a single updated row, so a plain
// invalidate-and-refetch is simpler and correct.
export function useBulkDeactivateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => apiClient.post('/admin/users/bulk-deactivate', { ids }),
    onSuccess: () => invalidateStaffCaches(queryClient),
  });
}

export function useBulkReactivateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => apiClient.post('/admin/users/bulk-reactivate', { ids }),
    onSuccess: () => invalidateStaffCaches(queryClient),
  });
}

export function useBulkDeleteStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, confirmedCount }) =>
      apiClient.post('/admin/users/bulk-delete', { ids, confirmedCount }),
    onSuccess: () => invalidateStaffCaches(queryClient),
  });
}

// GET /admin/users/bulk-delete-preview?ids=1,2,3 — called imperatively (not
// a persistent query) right before the confirm dialog opens, see
// utils/bulkActions.js#confirmAndBulkDelete.
export function fetchStaffBulkDeletePreview(ids) {
  return apiClient.get('/admin/users/bulk-delete-preview', { ids: ids.join(',') });
}

// Applies a granted/revoked permission to one user row, immutably, inside
// whichever of the two staff caches holds it — a plain mutation of
// user.permissions in place would keep the same array/object reference and
// React would never see the row as changed.
function withPermission(prev, id, permission, granted) {
  if (!prev || !prev.users) return prev;
  return {
    ...prev,
    users: prev.users.map((user) => {
      if (user.id !== id) return user;
      const held = new Set(user.permissions || []);
      if (granted) held.add(permission);
      else held.delete(permission);
      return { ...user, permissions: Array.from(held) };
    }),
  };
}

// PUT /admin/users/:id/permissions — { permission, granted }. Grants or
// revokes one per-user permission (migration 016 — currently only
// 'can_approve_revisions'). Idempotent server-side. Returns { user } with
// the refreshed permissions array.
//
// Optimistic: flips the checkbox in BOTH staff caches (USERS_KEY — the
// assignee-picker's cache, which the same GET also populates with a
// permissions array — and STAFF_USERS_KEY, which is what UsersPage.jsx
// actually renders) the instant the toggle fires, before the request
// resolves. A prior version only patched USERS_KEY, so the checkbox on the
// Users screen (reading STAFF_USERS_KEY) never visibly changed until an
// unrelated refetch — the change was saved, just invisible.
//
// This hook is shared by every permission checkbox on every row (see
// PermissionToggles in UsersPage.jsx), so two sibling permissions — even on
// the SAME user — can be in flight at once. onSuccess/onError deliberately
// touch only the single `permission` key named in that call's own
// `variables` (TanStack gives each mutate() call its own variables/context
// even though they share one mutation hook instance) — never the whole row
// — so confirming or rolling back one permission can't stomp a sibling
// permission's own concurrent optimistic flip on the same row. A
// whole-row snapshot-restore (an earlier version of this fix) would have:
// onError could restore a snapshot taken before a sibling's toggle landed,
// erasing it; onSuccess could overwrite the row with a server response that
// predates a sibling's still-in-flight request.
//
// mutationKey + the isMutating guard in onSettled below close the same gap
// one level up, at the cache-invalidation step: if toggle A settles while
// toggle B is still in flight, an unconditional invalidate would refetch
// and get back server state that doesn't include B yet, overwriting B's
// still-optimistic flip (a visible flicker). Only the LAST mutation with
// this key still running is allowed to invalidate — isMutating() is
// checked === 1, not 0, because the current (settling) call still counts
// itself while onSettled runs.
const SET_PERMISSION_MUTATION_KEY = ['admin', 'setUserPermission'];

export function useSetUserPermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: SET_PERMISSION_MUTATION_KEY,
    mutationFn: ({ id, permission, granted }) =>
      apiClient.put(`/admin/users/${id}/permissions`, { permission, granted }),
    onMutate: async ({ id, permission, granted }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: USERS_KEY }),
        queryClient.cancelQueries({ queryKey: STAFF_USERS_KEY }),
      ]);
      queryClient.setQueryData(USERS_KEY, (prev) => withPermission(prev, id, permission, granted));
      queryClient.setQueryData(STAFF_USERS_KEY, (prev) =>
        withPermission(prev, id, permission, granted)
      );
    },
    // Reconcile with the server's authoritative answer for THIS permission
    // only (is it present in the returned array?) rather than replacing the
    // whole permissions array, which could discard a sibling's own pending
    // optimistic change.
    onSuccess: (data, variables) => {
      const confirmedGranted = (data.user.permissions || []).includes(variables.permission);
      queryClient.setQueryData(USERS_KEY, (prev) =>
        withPermission(prev, variables.id, variables.permission, confirmedGranted)
      );
      queryClient.setQueryData(STAFF_USERS_KEY, (prev) =>
        withPermission(prev, variables.id, variables.permission, confirmedGranted)
      );
    },
    // Roll back ONLY this permission to its pre-toggle value (the inverse of
    // what we optimistically set) instead of restoring a whole-row snapshot.
    onError: (_err, variables) => {
      queryClient.setQueryData(USERS_KEY, (prev) =>
        withPermission(prev, variables.id, variables.permission, !variables.granted)
      );
      queryClient.setQueryData(STAFF_USERS_KEY, (prev) =>
        withPermission(prev, variables.id, variables.permission, !variables.granted)
      );
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: SET_PERMISSION_MUTATION_KEY }) === 1) {
        invalidateStaffCaches(queryClient);
      }
    },
  });
}

// GET /admin/users/permissions — the grantable-permission catalogue itself
// (utils/permissionCatalog.js), an ordered array of permission keys.
// pages/UsersPage.jsx renders whatever this returns instead of a hardcoded
// local list, so a new permission needs no frontend change beyond its two
// i18n strings (`users.permission.<key>.label` / `.hint`). Effectively
// static (it only changes with a deploy), so a long staleTime avoids
// refetching it on every UsersPage mount.
export function usePermissionCatalog() {
  return useQuery({
    queryKey: ['admin', 'permissionCatalog'],
    queryFn: () => apiClient.get('/admin/users/permissions'),
    select: (data) => data.permissions,
    staleTime: Infinity,
  });
}

export default useAdminUsers;
